'use client';

import { Avatar, AvatarFallback, Inline, Stack, Text } from '@cortex/ui';
import { Bot, Check, User } from 'lucide-react';

import { cn } from '@/lib/utils';

import type {
  ConfirmationQuestionProps,
  ConversationEntry,
  MultiSelectQuestionProps,
  QuestionOption,
  SingleSelectQuestionProps,
  SummaryConfirmationProps,
  TextQuestionProps,
} from './types';

interface ChatMessageProps {
  entry: ConversationEntry;
}

/**
 * Displays a completed Q&A exchange in a chat bubble style
 * Shows the AI's question and the user's answer as read-only history
 */
export function ChatMessage({ entry }: ChatMessageProps) {
  const { question, answer } = entry;

  // Extract display text from the question data
  const getQuestionText = (): string => {
    switch (question.questionType) {
      case 'ask_single_select':
      case 'ask_multi_select':
      case 'ask_confirmation':
      case 'ask_text':
        return (
          question.data as
            | SingleSelectQuestionProps
            | MultiSelectQuestionProps
            | ConfirmationQuestionProps
            | TextQuestionProps
        ).question;
      case 'present_summary':
        return (
          (question.data as SummaryConfirmationProps).confirmationQuestion ||
          'Please review the summary above'
        );
      default:
        return 'Question';
    }
  };

  // Get acknowledgment if present
  const getAcknowledgment = (): string | undefined => {
    const data = question.data as { acknowledgment?: string };
    return data.acknowledgment;
  };

  // Format the answer for display
  const formatAnswer = (): string => {
    // Handle structured answers that were stringified
    if (answer.startsWith('{') || answer.startsWith('[')) {
      try {
        const parsed = JSON.parse(answer);
        if (parsed.selections) {
          return (parsed.selections as string[]).join(', ');
        }
        if (typeof parsed.confirmed === 'boolean') {
          return parsed.confirmed ? 'Yes' : 'No';
        }
        if (parsed.action) {
          return parsed.action === 'confirm' ? 'Confirmed' : 'Edit requested';
        }
        return answer;
      } catch {
        return answer;
      }
    }

    // For single select, find the label if it matches an option ID
    if (question.questionType === 'ask_single_select') {
      const options = (question.data as SingleSelectQuestionProps).options;
      const matchingOption = options.find(
        (opt: QuestionOption) => opt.id === answer
      );
      if (matchingOption) {
        return matchingOption.label;
      }
    }

    return answer;
  };

  const acknowledgment = getAcknowledgment();
  const questionText = getQuestionText();
  const displayAnswer = formatAnswer();

  return (
    <Stack gap="sm" className="animate-in fade-in duration-200">
      {/* AI Message (Question) */}
      <Inline gap="sm" align="start">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="bg-primary/10">
            <Bot className="text-primary h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <Stack gap="xs" className="flex-1">
          {acknowledgment && (
            <Text color="muted" size="sm" className="italic">
              {acknowledgment}
            </Text>
          )}
          <Stack
            gap="none"
            className="bg-muted/50 rounded-lg rounded-tl-none px-4 py-3"
          >
            <Text>{questionText}</Text>
          </Stack>
        </Stack>
      </Inline>

      {/* User Message (Answer) */}
      <Inline gap="sm" justify="end">
        <Inline justify="end" className="flex-1">
          <Stack
            gap="none"
            className={cn(
              'rounded-lg rounded-tr-none px-4 py-3',
              'bg-primary text-primary-foreground',
              'max-w-[85%]'
            )}
          >
            <Inline gap="sm" align="center">
              <Check className="h-4 w-4 shrink-0 opacity-70" />
              <Text>{displayAnswer}</Text>
            </Inline>
          </Stack>
        </Inline>
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="bg-muted">
            <User className="text-foreground/60 h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      </Inline>
    </Stack>
  );
}
